/**
 * Phone Number Validation Helper
 * Supports international phone numbers including Georgia, UAE, and other regions
 */

/**
 * Validates phone number in E.164 format
 * E.164 format: +[country code][subscriber number]
 * @param {string} phoneNumber - Phone number to validate
 * @returns {boolean} - True if valid
 */
export const isValidPhoneNumber = (phoneNumber) => {
  if (!phoneNumber) return false;
  
  // E.164 format: + followed by country code (1-3 digits) and number (up to 15 total digits)
  const e164Regex = /^\+[1-9]\d{1,14}$/;
  return e164Regex.test(phoneNumber);
};

/**
 * Phone number examples by country
 * Use these for placeholder text or error messages
 */
export const phoneNumberExamples = {
  georgia: "+995555123456",
  uae: "+971501234567",
  saudi: "+966501234567",
  turkey: "+905551234567",
  qatar: "+97433123456",
  kuwait: "+96550012345",
  bahrain: "+97333123456",
  oman: "+96891234567",
  usa: "+11234567890",
  uk: "+447700900000",
  india: "+911234567890",
  russia: "+79161234567",
};

/**
 * Country codes for supported regions
 */
export const countryCodes = {
  georgia: "+995",
  uae: "+971",
  saudi: "+966",
  turkey: "+90",
  qatar: "+974",
  kuwait: "+965",
  bahrain: "+973",
  oman: "+968",
  usa: "+1",
  uk: "+44",
  india: "+91",
  russia: "+7",
};

/**
 * Format phone number for display
 * Removes any non-digit characters except the leading +
 * @param {string} phoneNumber - Phone number to format
 * @returns {string} - Formatted phone number
 */
export const formatPhoneNumber = (phoneNumber) => {
  if (!phoneNumber) return "";
  
  // Remove all non-digits except the leading +
  let cleaned = phoneNumber.replace(/[^\d+]/g, "");
  
  // Ensure it starts with +
  if (!cleaned.startsWith("+")) {
    cleaned = `+${cleaned}`;
  }
  
  // Remove any + signs that are not at the start
  cleaned = cleaned[0] + cleaned.slice(1).replace(/\+/g, "");
  
  return cleaned;
};

/**
 * Get country from phone number
 * @param {string} phoneNumber - Phone number with country code
 * @returns {string|null} - Country name or null if not recognized
 */
export const getCountryFromPhoneNumber = (phoneNumber) => {
  if (!phoneNumber || !phoneNumber.startsWith("+")) return null;
  
  // Extract country code (up to 4 digits after +)
  const codeMatch = phoneNumber.match(/^\+(\d{1,4})/);
  if (!codeMatch) return null;
  
  const code = `+${codeMatch[1]}`;
  
  // Find matching country
  for (const [country, countryCode] of Object.entries(countryCodes)) {
    if (code.startsWith(countryCode)) {
      return country;
    }
  }
  
  return null;
};

/**
 * Validate phone number and return detailed error message
 * @param {string} phoneNumber - Phone number to validate
 * @returns {Object} - { isValid: boolean, error: string|null }
 */
export const validatePhoneNumberWithMessage = (phoneNumber) => {
  if (!phoneNumber) {
    return {
      isValid: false,
      error: "Please enter a phone number",
    };
  }
  
  if (!phoneNumber.startsWith("+")) {
    return {
      isValid: false,
      error: `Phone number must start with country code (e.g., ${phoneNumberExamples.georgia} for Georgia, ${phoneNumberExamples.uae} for UAE)`,
    };
  }
  
  // Check if it's only + or too short
  if (phoneNumber.length < 8) {
    return {
      isValid: false,
      error: "Phone number is too short. Please include the full number with country code.",
    };
  }
  
  // Check if it's too long
  if (phoneNumber.length > 16) {
    return {
      isValid: false,
      error: "Phone number is too long. Please check the number.",
    };
  }
  
  // Check for invalid characters
  if (!/^\+\d+$/.test(phoneNumber)) {
    return {
      isValid: false,
      error: "Phone number should only contain digits after the + sign.",
    };
  }
  
  // Validate E.164 format
  if (!isValidPhoneNumber(phoneNumber)) {
    return {
      isValid: false,
      error: `Invalid phone number format. Examples: ${phoneNumberExamples.georgia} (Georgia), ${phoneNumberExamples.uae} (UAE)`,
    };
  }
  
  return {
    isValid: true,
    error: null,
  };
};

/**
 * Get friendly error message for phone number validation
 * Includes regional examples based on detected country
 * @param {string} phoneNumber - Phone number to validate
 * @returns {string|null} - Error message or null if valid
 */
export const getPhoneNumberErrorMessage = (phoneNumber) => {
  const validation = validatePhoneNumberWithMessage(phoneNumber);
  return validation.error;
};

/**
 * Parse phone number into components
 * @param {string} phoneNumber - Phone number to parse
 * @returns {Object} - { countryCode: string, number: string, country: string|null }
 */
export const parsePhoneNumber = (phoneNumber) => {
  if (!phoneNumber || !phoneNumber.startsWith("+")) {
    return { countryCode: null, number: null, country: null };
  }
  
  const country = getCountryFromPhoneNumber(phoneNumber);
  const countryCode = country ? countryCodes[country] : null;
  
  if (countryCode) {
    return {
      countryCode,
      number: phoneNumber.slice(countryCode.length),
      country,
    };
  }
  
  // If country not recognized, try to extract first 1-4 digits as country code
  const match = phoneNumber.match(/^\+(\d{1,4})(\d+)$/);
  if (match) {
    return {
      countryCode: `+${match[1]}`,
      number: match[2],
      country: null,
    };
  }
  
  return { countryCode: null, number: null, country: null };
};

/**
 * Format phone number for display with country name
 * @param {string} phoneNumber - Phone number to format
 * @returns {string} - Formatted string like "+995555123456 (Georgia)"
 */
export const formatPhoneNumberWithCountry = (phoneNumber) => {
  const country = getCountryFromPhoneNumber(phoneNumber);
  const formatted = formatPhoneNumber(phoneNumber);
  
  if (country) {
    const countryName = country.charAt(0).toUpperCase() + country.slice(1);
    return `${formatted} (${countryName})`;
  }
  
  return formatted;
};

